import BlogHeader from "@/components/blog/BlogHeader";
import GettingHereMap from "@/components/GettingHereMap";
import heroVideoAsset from "@/assets/Sequence_04.mp4.asset.json";

const GMAPS_DIRECTIONS =
  "https://www.google.com/maps/dir/?api=1" +
  "&origin=35.25766941170577%2C-112.1222817560173" +
  "&destination=6929+E+Rosilda+Springs+Rd%2C+Williams%2C+AZ+86046" +
  "&waypoints=35.182292%2C-112.032942%7C35.165351%2C-112.039490%7C35.169999%2C-112.053882" +
  "&travelmode=driving";

export default function GettingHerePage() {
  return (
    <div
      className="fixed inset-0 overflow-y-auto bg-background text-foreground"
      style={{ touchAction: "auto", overscrollBehavior: "auto" }}
    >
      <BlogHeader backOverride={{ to: "/info", label: "← INFO" }} />

      {/* Hero */}
      <header className="relative w-full max-w-3xl mx-auto h-[60vh] min-h-[420px] overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          poster=""
          className="absolute inset-0 w-full h-full object-cover object-bottom"
        >
          <source src={heroVideoAsset.url} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/80" />
        <div className="absolute inset-0 vignette" />
        <div className="relative z-10 h-full flex flex-col items-center justify-end text-center px-6 pb-14 max-w-3xl mx-auto">
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold/90 mb-3">
            The Journey In
          </div>
          <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] text-white text-shadow-soft">
            Getting to Backland
          </h1>
          <p className="mt-6 font-serif italic text-base md:text-lg text-white/85 max-w-xl">
            “And into the forest I go, to find my soul.”
            <span className="block not-italic text-xs tracking-widest uppercase text-white/55 mt-2">
              — John Muir
            </span>
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-14 md:py-20 space-y-14">
        <section className="space-y-5 text-foreground/85 leading-relaxed text-base md:text-lg">
          <p>
            Backland is tucked away on 160 private acres, surrounded by pristine National Forest.
            The final stretch of the drive takes you off the pavement and into the quiet —
            through ponderosa pines, open meadows, and the kind of stillness you only find
            when the world drops away.
          </p>
          <p className="text-foreground/70">
            Before you leave Williams, open the guided voice tour we'll send you. It'll walk
            you through the turns, the landmarks, and the little stories of the land you're
            about to call home for a few days.
          </p>
        </section>

        <section>
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold/85 mb-3">
            What to Expect
          </div>
          <h2 className="font-serif text-2xl md:text-3xl mb-4">A short drive off the pavement</h2>
          <ul className="space-y-3 text-foreground/80 leading-relaxed text-sm md:text-base list-disc pl-5 marker:text-gold/70">
            <li>
              The final 8 miles are on a well-maintained gravel forest road — passable in any
              standard vehicle in normal conditions.
            </li>
            <li>
              With snow or ice take it slow. AWD is required in these conditions.
            </li>
            <li>
              You will likely encounter wash boarding and shallow puddles in wet conditions.
              There are two concrete low water crossings that occasionally have shallow flow
              after heavy rain or snow melt.
            </li>
            <li>
              Very low-clearance sports cars or road motorcycles are not recommended.
            </li>
          </ul>
        </section>

        {/* Map */}
        <section>
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold/85 mb-3">
            The Route
          </div>
          <h2 className="font-serif text-2xl md:text-3xl mb-5">From I-40 Exit 167 to Backland</h2>
          <GettingHereMap />
          <div className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <a
              href={GMAPS_DIRECTIONS}
              onClick={(e) => {
                e.preventDefault();
                window.open(GMAPS_DIRECTIONS, "_blank", "noopener,noreferrer");
              }}
              className="inline-flex items-center justify-center font-serif italic text-base text-background bg-gold/95 px-7 py-3 rounded-full tracking-wide hover:bg-gold transition"
            >
              Open in Google Maps
            </a>
            <p className="text-xs text-foreground/55 sm:ml-2">
              Opens with our recommended route already plotted.
            </p>
          </div>
        </section>


        <section className="border-t border-border/60 pt-12">
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold/85 mb-3">
            Worth the Drive
          </div>
          <h2 className="font-serif text-2xl md:text-3xl mb-4">Once you arrive</h2>
          <p className="font-serif italic text-lg md:text-xl text-foreground/85 leading-relaxed">
            By the time you arrive, the highway noise, city lights, and crowds are gone.
            Just forest, sky, stillness — and the feeling that you've found somewhere truly
            special.
          </p>
        </section>
      </main>
    </div>
  );
}
